// src/pages/login/loginPage.js
import React, { useCallback, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button } from "antd";
import { confirmSignIn, fetchAuthSession, signIn, signOut } from "aws-amplify/auth";
import "@fontsource/oswald";
import "./loginPage.css";

// Initial state for useReducer
const initialState = {
  id: "",
  password: "",
  errorMessage: "",
  successMessage: "",
  isLoading: false
};

// Reducer for state management
const loginReducer = (state, action) => {
  switch (action.type) {
    case "setId":
      return { ...state, id: action.payload };
    case "setPassword":
      return { ...state, password: action.payload };
    case "setErrorMessage":
      return { ...state, errorMessage: action.payload, successMessage: "" };
    case "setSuccessMessage":
      return { ...state, successMessage: action.payload, errorMessage: "" };
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "clearMessages":
      return { ...state, errorMessage: "", successMessage: "" };
    case "resetForm":
      return initialState;
    default:
      return state;
  }
};

function Login() {
  const [state, dispatch] = useReducer(loginReducer, initialState);
  const [newPassword, setNewPassword] = useState("");
  const [requireNewPassword, setRequireNewPassword] = useState(false);

  const navigate = useNavigate();

  const { id, password, errorMessage, successMessage, isLoading } = state;

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      dispatch({ type: "clearMessages" });
      dispatch({ type: "setLoading", payload: true });

      try {
        // Check if already signed in
        try {
          const session = await fetchAuthSession();
          if (session && session.tokens?.idToken) {
            await signOut();
          }
        } catch (err) {
          // No session, continue
        }

        const result = await signIn({ username: id, password });

        if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          setRequireNewPassword(true);
          dispatch({
            type: "setSuccessMessage",
            payload: "Please set a new password to continue."
          });
          dispatch({ type: "setLoading", payload: false });
          return;
        }

        // Get tokens & groups
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const accessToken = session.tokens?.accessToken?.toString();

        console.log("Access Token:", accessToken);

        if (!idToken) {
          throw new Error("Could not retrieve ID token");
        }

        // Decode JWT to extract groups
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        const groups = payload["cognito:groups"] || [];

        console.log("🎯 Full JWT payload:", payload);
        console.log("🎯 User groups from JWT:", groups);

        // Filter out "user" group to get admin roles only
        const adminGroups = groups.filter((group) => group !== "user");

        console.log("🎯 Admin groups:", adminGroups);

        // Check if user has any admin roles
        if (adminGroups.length === 0) {
          // User only has "user" group or no groups - block access
          localStorage.removeItem("role");
          localStorage.removeItem("groups");
          console.warn("Access denied - no admin groups detected");
          dispatch({
            type: "setErrorMessage",
            payload: "You don't have admin access. Please use the mobile application or contact admin."
          });
        } else {
          // User has admin roles - allow access
          const primaryAdminRole = adminGroups[0]; // First admin role

          // Store both all groups and primary admin role
          localStorage.setItem("groups", JSON.stringify(groups));
          localStorage.setItem("adminGroups", JSON.stringify(adminGroups));
          localStorage.setItem("role", primaryAdminRole); // Store admin role, not "user"

          console.log("✅ Login successful:", {
            allGroups: groups,
            adminGroups: adminGroups,
            primaryRole: primaryAdminRole
          });

          dispatch({ type: "setSuccessMessage", payload: "Login successful." });
          setTimeout(() => {
            navigate("/home", { replace: true });
          }, 1000);
        }
      } catch (error) {
        console.error("Login error:", error);
        let message = "Login failed. Please try again.";

        if (error.name === "UserNotFoundException") {
          message = "User does not exist.";
        } else if (error.name === "NotAuthorizedException") {
          message = "Incorrect username or password.";
        } else if (error.message) {
          message = error.message;
        }

        dispatch({ type: "setErrorMessage", payload: message });
      } finally {
        dispatch({ type: "setLoading", payload: false });
      }
    },
    [id, password, navigate]
  );

  const handleIdChange = useCallback((e) => {
    dispatch({ type: "setId", payload: e.target.value });
  }, []);

  const handlePasswordChange = useCallback((e) => {
    dispatch({ type: "setPassword", payload: e.target.value });
  }, []);

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: "setLoading", payload: true });
    dispatch({ type: "setErrorMessage", payload: "" });

    try {
      await confirmSignIn({
        challengeResponse: newPassword
      });

      dispatch({
        type: "setSuccessMessage",
        payload: "Password updated successfully. Logging in..."
      });
      setRequireNewPassword(false);

      setTimeout(() => {
        navigate("/home", { replace: true });
      }, 1000);
    } catch (err) {
      console.error(err);
      dispatch({
        type: "setErrorMessage",
        payload: err.message || "Failed to update password"
      });
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  };

  return (
    <div className="lp-container">
      <div className="lp-left-section">
        <div className="lp-logo-container">
          <img src="images/OurChurchLink Logo Final Trans (1024x1024).png" alt="App Logo" className="lp-logo" />
          <img src="images/OurChurchLink Logo Tagline Trans (1000 x 300 px).png" alt="App Logo" className="lp-app-text" />
        </div>
      </div>

      <div className="lp-right-section">
        <div className="lp-login-form">
          <h2 className="lp-header">Admin Login</h2>
          <p className="lp-para">If you are already an admin, you may login with your ID and password.</p>

          {!requireNewPassword ? (
            <form onSubmit={handleSubmit}>
              <div className="lp-input-group">
                <label>ID:</label>
                <Input className="lp-text-field" type="text" value={id} onChange={handleIdChange} placeholder="Enter your ID" size="large" required disabled={isLoading} />
              </div>

              <div className="lp-input-group">
                <label>Password:</label>
                <Input.Password
                  className="lp-password-field"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  size="large"
                  required
                  disabled={isLoading}
                />
              </div>

              <Button className="lp-button" htmlType="submit" loading={isLoading} size="large" block>
                {isLoading ? "Logging in..." : "Login Account"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleNewPasswordSubmit}>
              <div className="lp-input-group">
                <label>New Password:</label>
                <Input.Password
                  className="lp-password-field"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  size="large"
                  required
                  disabled={isLoading}
                />
              </div>

              <Button className="lp-button" htmlType="submit" loading={isLoading} size="large" block>
                {isLoading ? "Updating..." : "Set New Password"}
              </Button>
            </form>
          )}

          {successMessage && <p className="lp-success-message">{successMessage}</p>}
          {errorMessage && <p className="lp-error-message">{errorMessage}</p>}
        </div>
      </div>
    </div>
  );
}

export default Login;
